import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Search,
  Refresh
} from '@mui/icons-material';
import axios from 'axios';
import { notify } from '../notifications';

interface OFDUser {
  id: number;
  username: string;
  ofdLogin: string;
  ofdPassword: string;
  browserId: string;
  lastUpd: string;
  userUpd: string;
}

interface User {
  id: number;
  username: string;
  pharmacyNum: string;
}

const OFDManagement: React.FC = () => {
  const [ofdUsers, setOfdUsers] = useState<OFDUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<OFDUser[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Диалог создания/редактирования
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState<Partial<OFDUser>>({});
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Диалог подтверждения удаления
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  
  // Диалог просмотра пароля
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [decryptedPassword, setDecryptedPassword] = useState('');
  
  // Видимость паролей
  const [visiblePasswords, setVisiblePasswords] = useState<Set<number>>(new Set());
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isAuthorizedToViewPasswords, setIsAuthorizedToViewPasswords] = useState(false);

  useEffect(() => {
    fetchOfdUsers();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(ofdUsers);
    } else {
      const filtered = ofdUsers.filter(user =>
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.ofdLogin.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, ofdUsers]);

  const fetchOfdUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://tbl.zerdegroup.kz/api/ofd/all');
      if (response.data.success) {
        setOfdUsers(response.data.data);
        setFilteredUsers(response.data.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      notify.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/users-with-pharmacy');
      if (response.data.success) {
        setAllUsers(response.data.users);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    }
  };

  const handleOpenDialog = (user?: OFDUser) => {
    if (user) {
      setEditMode(true);
      setCurrentUser(user);
      // Найти соответствующего пользователя в списке всех пользователей
      const foundUser = allUsers.find(u => u.username === user.username);
      setSelectedUser(foundUser || null);
    } else {
      setEditMode(false);
      setCurrentUser({});
      setSelectedUser(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentUser({});
    setSelectedUser(null);
    setShowNewPassword(false);
  };

  const handleSave = async () => {
    const currentUsername = localStorage.getItem('username') || '';
    
    // Проверка обязательных полей
    if (!editMode && !selectedUser) {
      notify.error('Выберите пользователя');
      return;
    }
    
    try {
      if (editMode && currentUser.id) {
        await axios.put(`https://tbl.zerdegroup.kz/api/ofd/update/${currentUser.id}`, {
          ...currentUser,
          userUpd: currentUsername
        });
        notify.success('Запись успешно обновлена');
      } else {
        await axios.post('https://tbl.zerdegroup.kz/api/ofd/create', {
          ...currentUser,
          username: selectedUser?.username,
          userUpd: currentUsername
        });
        notify.success('Запись успешно создана');
      }
      handleCloseDialog();
      fetchOfdUsers();
    } catch (error: any) {
      console.error('Ошибка сохранения:', error);
      notify.error(error.response?.data?.message || 'Ошибка сохранения');
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`https://tbl.zerdegroup.kz/api/ofd/delete/${userToDelete}`);
      notify.success('Запись успешно удалена');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchOfdUsers();
    } catch (error) {
      console.error('Ошибка удаления:', error);
      notify.error('Ошибка удаления');
    }
  };

  const handleShowPassword = async (userId: number) => {
    // Проверяем, виден ли уже пароль
    if (visiblePasswords.has(userId)) {
      // Скрыть пароль
      const newVisible = new Set(visiblePasswords);
      newVisible.delete(userId);
      setVisiblePasswords(newVisible);
    } else {
      // Если уже авторизован, сразу расшифровываем пароль
      if (isAuthorizedToViewPasswords) {
        await decryptPassword(userId);
      } else {
        // Показать диалог ввода пароля администратора
        setSelectedUserId(userId);
        setPasswordDialogOpen(true);
      }
    }
  };

  const decryptPassword = async (userId: number) => {
    try {
      const response = await axios.post('https://tbl.zerdegroup.kz/api/ofd/decrypt-password', {
        id: userId
      });

      if (response.data.success) {
        const newVisible = new Set(visiblePasswords);
        newVisible.add(userId);
        setVisiblePasswords(newVisible);
        
        // Сохраняем расшифрованный пароль во временном хранилище
        setOfdUsers(ofdUsers.map(u => 
          u.id === userId 
            ? { ...u, ofdPassword: response.data.password } 
            : u
        ));
      }
    } catch (error: any) {
      console.error('Ошибка расшифровки:', error);
      notify.error(error.response?.data?.message || 'Ошибка расшифровки пароля');
    }
  };

  const handlePasswordDialogSubmit = async () => {
    if (!selectedUserId || !adminPassword) return;

    try {
      // Проверяем пароль администратора через существующий API
      const authResponse = await axios.post('https://tbl.zerdegroup.kz/api/login', {
        login: localStorage.getItem('username'),
        password: adminPassword
      });

      if (!authResponse.data.success) {
        notify.error('Неверный пароль');
        return;
      }

      // Если пароль верный, устанавливаем авторизацию для просмотра паролей
      setIsAuthorizedToViewPasswords(true);

      // Расшифровываем ВСЕ пароли
      await Promise.all(ofdUsers.map(user => decryptPassword(user.id)));
      
      setPasswordDialogOpen(false);
      setAdminPassword('');
      setSelectedUserId(null);
    } catch (error: any) {
      console.error('Ошибка:', error);
      notify.error(error.response?.data?.message || 'Ошибка проверки пароля');
    }
  };

  const formatBrowserId = (id: string) => {
    if (id.length <= 8) return id;
    return `${id.substring(0, 5)}...${id.substring(id.length - 3)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Поиск по пользователю или логину ОФД"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchOfdUsers}
        >
          Обновить
        </Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Добавить
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Пользователь</TableCell>
              <TableCell>Логин ОФД</TableCell>
              <TableCell>Пароль ОФД</TableCell>
              <TableCell>ID расширения</TableCell>
              <TableCell>Обновлено</TableCell>
              <TableCell>Кем обновлено</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.ofdLogin}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {visiblePasswords.has(user.id) ? user.ofdPassword : '••••••••'}
                    <IconButton
                      size="small"
                      onClick={() => handleShowPassword(user.id)}
                    >
                      {visiblePasswords.has(user.id) ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={user.browserId}>
                    <span>{formatBrowserId(user.browserId)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{formatDate(user.lastUpd)}</TableCell>
                <TableCell>{user.userUpd || '-'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(user)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setUserToDelete(user.id);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">
                    {searchQuery ? 'Ничего не найдено' : 'Нет данных'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания/редактирования */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Редактировать запись' : 'Добавить запись'}</DialogTitle>
        <DialogContent>
          <Autocomplete<User>
            options={allUsers}
            getOptionLabel={(option: User) => `${option.username} (Аптека: ${option.pharmacyNum})`}
            renderInput={(params: any) => <TextField {...params} label="Пользователь (логин на сайте)" margin="dense" />}
            onChange={(event: any, value: User | null) => setSelectedUser(value)}
            value={selectedUser}
            disabled={editMode}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Логин ОФД"
            fullWidth
            value={currentUser.ofdLogin || ''}
            onChange={(e) => setCurrentUser({ ...currentUser, ofdLogin: e.target.value })}
          />
          <TextField
            margin="dense"
            label={editMode ? "Пароль ОФД (оставьте пустым, если не меняете)" : "Пароль ОФД"}
            fullWidth
            type={showNewPassword ? 'text' : 'password'}
            value={currentUser.ofdPassword || ''}
            onChange={(e) => setCurrentUser({ ...currentUser, ofdPassword: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            label="ID расширения браузера"
            fullWidth
            value={currentUser.browserId || ''}
            onChange={(e) => setCurrentUser({ ...currentUser, browserId: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Обновить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>Вы уверены, что хотите удалить эту запись?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог ввода пароля для просмотра */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>Введите ваш пароль</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Для просмотра пароля ОФД введите ваш пароль от системы
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Пароль"
            type="password"
            fullWidth
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordDialogSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPasswordDialogOpen(false);
            setAdminPassword('');
            setSelectedUserId(null);
          }}>
            Отмена
          </Button>
          <Button onClick={handlePasswordDialogSubmit} variant="contained">
            Показать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OFDManagement;

